import { FormEvent, useEffect, useState } from "react";
import { supabase } from "./lib/supabase";
import { CurrencyInput } from "./CurrencyInput";
import "./after-sales.css";
type Sale = {
  id: string;
  customer_name: string;
  sale_date: string;
  vehicle_id: string;
  registration: string | null;
  vehicle_name: string;
  has_warranty: boolean;
};
type Product = {
  id: string;
  name: string;
  duration_months: number;
  cost_cents: number;
  provider_id: string;
};
type Provider = { id: string; name: string };
type Warranty = {
  id: string;
  sale_id: string;
  warranty_type: string;
  product_name: string | null;
  provider_name: string | null;
  duration_months: number;
  starts_on: string;
  ends_on: string;
  cost_cents: number;
  currency_code: string;
  customer_name: string;
  registration: string | null;
  vehicle_name: string;
};
type Case = {
  id: string;
  sale_id: string;
  title: string;
  description: string;
  status: string;
  priority: string;
  due_at: string;
  assigned_to: string;
  resolution: string | null;
  customer_name: string;
  registration: string | null;
  vehicle_name: string;
  cost_total_cents: number;
  document_count: number;
  task_count: number;
};
type Member = { user_id: string; name: string; role_key: string };
const money = (c: number, code = "EUR") =>
  (c / 100).toLocaleString("pt-PT", { style: "currency", currency: code });
const safe = (value: string) =>
  value.normalize("NFKD").replace(/[^a-zA-Z0-9._-]/g, "-");
async function digest(file: File) {
  return [
    ...new Uint8Array(
      await crypto.subtle.digest("SHA-256", await file.arrayBuffer()),
    ),
  ]
    .map((v) => v.toString(16).padStart(2, "0"))
    .join("");
}
export function AfterSalesCenter({
  organizationId,
  canManage,
}: {
  organizationId: string;
  canManage: boolean;
}) {
  const [sales, setSales] = useState<Sale[]>([]),
    [products, setProducts] = useState<Product[]>([]),
    [providers, setProviders] = useState<Provider[]>([]),
    [warranties, setWarranties] = useState<Warranty[]>([]),
    [cases, setCases] = useState<Case[]>([]),
    [members, setMembers] = useState<Member[]>([]),
    [selected, setSelected] = useState<Case | null>(null),
    [notice, setNotice] = useState<string | null>(null),
    [busy, setBusy] = useState(false),
    [creating, setCreating] = useState(false),
    [warrantySale, setWarrantySale] = useState<Sale | null>(null);
  const load = async () => {
    const { data, error } = await supabase.rpc("after_sales_panel", {
      p_organization_id: organizationId,
    });
    if (error) return setNotice(error.message);
    setSales(data.sales ?? []);
    setProducts(data.products ?? []);
    setProviders(data.providers ?? []);
    setWarranties(data.warranties ?? []);
    setCases(data.cases ?? []);
    setMembers(data.members ?? []);
    setSelected((c) =>
      c ? ((data.cases ?? []).find((x: Case) => x.id === c.id) ?? null) : null,
    );
  };
  useEffect(() => {
    void load();
  }, [organizationId]);
  const run = async (
    action: () => PromiseLike<{ error: unknown }>,
    ok: string,
  ) => {
    setBusy(true);
    try {
      const { error } = await action();
      if (error) throw error;
      setNotice(ok);
      await load();
      return true;
    } catch (error) {
      setNotice(
        (error as { message?: string })?.message ?? "Não foi possível guardar.",
      );
      return false;
    } finally {
      setBusy(false);
    }
  };
  const setWarranty = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!warrantySale) return;
    const f = new FormData(e.currentTarget),
      type = String(f.get("type"));
    const saved = await run(
      () =>
        supabase.rpc("set_sale_warranty", {
          p_sale_id: warrantySale.id,
          p_warranty_type: type,
          p_product_id: type === "acquired" ? f.get("product") : null,
          p_duration_months: Number(f.get("months") || 18),
          p_starts_on: f.get("starts"),
        }),
      "Garantia associada à venda e margem atualizada.",
    );
    if (saved) setWarrantySale(null);
  };
  const createCase = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const saved = await run(
      () =>
        supabase.rpc("create_after_sales_case", {
          p_sale_id: f.get("sale"),
          p_title: f.get("title"),
          p_description: f.get("description"),
          p_priority: f.get("priority"),
          p_due_at: new Date(String(f.get("due"))).toISOString(),
          p_assigned_to: f.get("assigned"),
        }),
      "Ocorrência aberta e tarefa criada.",
    );
    if (saved) setCreating(false);
  };
  const updateCase = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selected) return;
    const f = new FormData(e.currentTarget);
    void run(
      () =>
        supabase.rpc("update_after_sales_case", {
          p_case_id: selected.id,
          p_status: f.get("status"),
          p_due_at: new Date(String(f.get("due"))).toISOString(),
          p_assigned_to: f.get("assigned"),
          p_resolution: f.get("resolution"),
        }),
      "Ocorrência atualizada e tarefa sincronizada.",
    );
  };
  const addCost = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selected) return;
    const f = new FormData(e.currentTarget);
    void run(
      () =>
        supabase.rpc("add_after_sales_cost", {
          p_case_id: selected.id,
          p_description: f.get("description"),
          p_amount_cents: Math.round(Number(f.get("amount")) * 100),
          p_state: f.get("state"),
        }),
      "Custo guardado e refletido na margem.",
    );
  };
  const upload = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selected) return;
    const f = new FormData(e.currentTarget),
      file = f.get("file") as File,
      path = `${organizationId}/after-sales/${selected.id}/${crypto.randomUUID()}-${safe(file.name)}`;
    setBusy(true);
    try {
      const sent = await supabase.storage
        .from("stand-document-archive")
        .upload(path, file);
      if (sent.error) throw sent.error;
      const { error } = await supabase.rpc("register_archive_document", {
        p_organization_id: organizationId,
        p_vehicle_id: null,
        p_entity_type: "after_sales_case",
        p_entity_id: selected.id,
        p_category: "Garantia",
        p_title: f.get("name") || file.name,
        p_storage_path: path,
        p_original_name: file.name,
        p_mime_type: file.type || "application/octet-stream",
        p_size_bytes: file.size,
        p_sha256: await digest(file),
        p_notes: f.get("notes"),
      });
      if (error) throw error;
      setNotice("Documento guardado no processo pós-venda.");
      await load();
    } catch (error) {
      await supabase.storage.from("stand-document-archive").remove([path]);
      setNotice(
        (error as { message?: string })?.message ??
          "Não foi possível guardar o documento.",
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="after-sales">
      <header>
        <div>
          <p className="eyebrow">Garantias e pós-venda</p>
          <h2>Acompanhamento depois da entrega</h2>
          <p>
            Garantias, ocorrências, tarefas, custos e documentos no mesmo
            processo.
          </p>
        </div>
        {canManage && (
          <button className="stock-primary" onClick={() => setCreating(true)}>
            Nova ocorrência
          </button>
        )}
      </header>
      {notice && (
        <button className="stock-notice" onClick={() => setNotice(null)}>
          {notice} ×
        </button>
      )}
      <div className="after-sales-summary">
        <article>
          <small>Garantias ativas</small>
          <strong>{warranties.length}</strong>
        </article>
        <article>
          <small>Ocorrências abertas</small>
          <strong>
            {
              cases.filter((c) => !["resolved", "closed"].includes(c.status))
                .length
            }
          </strong>
        </article>
        <article>
          <small>Custos registados</small>
          <strong>
            {money(cases.reduce((a, c) => a + Number(c.cost_total_cents), 0))}
          </strong>
        </article>
      </div>
      <section>
        <h3>Vendas e garantias</h3>
        <div className="after-sales-list">
          {sales.map((s) => {
            const w = warranties.find((x) => x.sale_id === s.id);
            return (
              <article key={s.id}>
                <div>
                  <strong>
                    {s.registration || s.vehicle_name} · {s.customer_name}
                  </strong>
                  <small>
                    {w
                      ? `${w.warranty_type === "own" ? "Garantia própria" : w.product_name} · até ${new Date(w.ends_on).toLocaleDateString("pt-PT")}`
                      : "Garantia ainda não definida"}
                  </small>
                </div>
                {canManage && (
                  <button
                    className="stock-secondary"
                    onClick={() => setWarrantySale(s)}
                  >
                    {w ? "Alterar garantia" : "Definir garantia"}
                  </button>
                )}
              </article>
            );
          })}
        </div>
      </section>
      <section>
        <h3>Ocorrências</h3>
        <div className="after-sales-list">
          {cases.map((c) => (
            <button key={c.id} onClick={() => setSelected(c)}>
              <i data-priority={c.priority} />
              <span>
                <strong>{c.title}</strong>
                <small>
                  {c.registration || c.vehicle_name} · {c.customer_name}
                </small>
              </span>
              <span>
                <b>{c.status.replaceAll("_", " ")}</b>
                <small>
                  {c.task_count} tarefa · {c.document_count} documento ·{" "}
                  {money(Number(c.cost_total_cents))}
                </small>
              </span>
            </button>
          ))}
        </div>
      </section>
      {warrantySale && (
        <div className="stock-dialog-backdrop">
          <section className="stock-dialog">
            <header>
              <div>
                <p className="eyebrow">Venda</p>
                <h2>Definir garantia</h2>
                <p>
                  {warrantySale.vehicle_name} · {warrantySale.customer_name}
                </p>
              </div>
              <button
                className="stock-close"
                onClick={() => setWarrantySale(null)}
              >
                ×
              </button>
            </header>
            <form onSubmit={setWarranty}>
              <label>
                <span>Tipo</span>
                <select name="type" defaultValue="own">
                  <option value="own">Garantia própria</option>
                  <option value="acquired">Garantia adquirida</option>
                </select>
              </label>
              <label>
                <span>Produto adquirido</span>
                <select name="product" defaultValue="">
                  <option value="">Escolha quando aplicável</option>
                  {products
                    .filter((p) => p.cost_cents >= 0)
                    .map((p) => (
                      <option key={p.id} value={p.id}>
                        {providers.find((x) => x.id === p.provider_id)?.name} ·{" "}
                        {p.name} · {money(p.cost_cents)}
                      </option>
                    ))}
                </select>
              </label>
              <label>
                <span>Início</span>
                <input
                  name="starts"
                  type="date"
                  defaultValue={warrantySale.sale_date}
                  required
                />
              </label>
              <label>
                <span>Meses da garantia própria</span>
                <input
                  name="months"
                  type="number"
                  min="1"
                  max="120"
                  defaultValue="18"
                />
              </label>
              <footer>
                <button
                  type="button"
                  className="stock-secondary"
                  onClick={() => setWarrantySale(null)}
                >
                  Cancelar
                </button>
                <button className="stock-primary" disabled={busy}>
                  Guardar
                </button>
              </footer>
            </form>
          </section>
        </div>
      )}
      {creating && (
        <div className="stock-dialog-backdrop">
          <section className="stock-dialog">
            <header>
              <div>
                <p className="eyebrow">Pós-venda</p>
                <h2>Nova ocorrência</h2>
              </div>
              <button
                className="stock-close"
                onClick={() => setCreating(false)}
              >
                ×
              </button>
            </header>
            <form onSubmit={createCase}>
              <label>
                <span>Venda</span>
                <select name="sale" required defaultValue="">
                  <option value="" disabled>
                    Escolha a venda
                  </option>
                  {sales.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.registration || s.vehicle_name} · {s.customer_name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Título</span>
                <input name="title" required />
              </label>
              <label>
                <span>Responsável</span>
                <select name="assigned" required defaultValue="">
                  <option value="" disabled>
                    Escolha
                  </option>
                  {members.map((m) => (
                    <option key={m.user_id} value={m.user_id}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Prioridade</span>
                <select name="priority">
                  <option value="normal">Normal</option>
                  <option value="high">Alta</option>
                  <option value="urgent">Urgente</option>
                  <option value="low">Baixa</option>
                </select>
              </label>
              <label>
                <span>Prazo</span>
                <input name="due" type="datetime-local" required />
              </label>
              <label className="wide">
                <span>Descrição</span>
                <textarea name="description" required />
              </label>
              <footer>
                <button
                  type="button"
                  className="stock-secondary"
                  onClick={() => setCreating(false)}
                >
                  Cancelar
                </button>
                <button className="stock-primary" disabled={busy}>
                  Guardar
                </button>
              </footer>
            </form>
          </section>
        </div>
      )}
      {selected && (
        <div className="stock-dialog-backdrop">
          <section className="stock-dialog after-sales-dialog">
            <header>
              <div>
                <p className="eyebrow">Processo pós-venda</p>
                <h2>{selected.title}</h2>
                <p>
                  {selected.registration || selected.vehicle_name} ·{" "}
                  {selected.customer_name}
                </p>
              </div>
              <button className="stock-close" onClick={() => setSelected(null)}>
                ×
              </button>
            </header>
            <p>{selected.description}</p>
            <form onSubmit={updateCase}>
              <label>
                <span>Estado</span>
                <select name="status" defaultValue={selected.status}>
                  <option value="open">Aberta</option>
                  <option value="in_progress">Em tratamento</option>
                  <option value="awaiting_customer">Aguarda cliente</option>
                  <option value="awaiting_supplier">Aguarda fornecedor</option>
                  <option value="resolved">Resolvida</option>
                  <option value="closed">Fechada</option>
                </select>
              </label>
              <label>
                <span>Responsável</span>
                <select name="assigned" defaultValue={selected.assigned_to}>
                  {members.map((m) => (
                    <option key={m.user_id} value={m.user_id}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Prazo</span>
                <input
                  name="due"
                  type="datetime-local"
                  defaultValue={selected.due_at.slice(0, 16)}
                />
              </label>
              <label className="wide">
                <span>Resolução</span>
                <textarea
                  name="resolution"
                  defaultValue={selected.resolution ?? ""}
                  placeholder="Obrigatória ao resolver ou fechar"
                />
              </label>
              <button className="stock-primary" disabled={!canManage || busy}>
                Guardar
              </button>
            </form>
            <div className="after-sales-actions">
              <form onSubmit={addCost}>
                <h3>Adicionar custo</h3>
                <input name="description" placeholder="Descrição" required />
                <CurrencyInput
                  currencyCode="EUR"
                  name="amount"
                  type="number"
                  min="0.01"
                  step="0.01"
                  placeholder="Valor"
                  required
                />
                <select name="state">
                  <option value="planned">Previsto</option>
                  <option value="committed">Comprometido</option>
                  <option value="invoiced">Faturado</option>
                  <option value="paid">Pago</option>
                </select>
                <button className="stock-primary" disabled={!canManage || busy}>
                  Guardar
                </button>
              </form>
              <form onSubmit={upload}>
                <h3>Adicionar documento</h3>
                <input
                  name="file"
                  type="file"
                  accept="application/pdf,image/jpeg,image/png,image/webp"
                  required
                />
                <input name="name" placeholder="Nome do ficheiro" />
                <textarea name="notes" placeholder="Notas" />
                <button className="stock-primary" disabled={!canManage || busy}>
                  Guardar
                </button>
              </form>
            </div>
          </section>
        </div>
      )}
    </section>
  );
}
